import {
  DEFAULT_RAID_DURATION_MINUTES,
  ForestLocationKey,
  type ForestLocation,
  type MapTagObjective,
  type ForestSpawnSlot,
} from '../types/index.ts'

export const FOREST_SPAWN_INTERVAL_MINUTES = 5

/**
 * Fixed map anchors expressed in basis points so the overlay scales with the
 * map image. These are presentation defaults; persisted group positions use
 * the same coordinate system but remain fully user-controlled.
 */
export const FOREST_LOCATIONS: ReadonlyArray<ForestLocation> = Object.freeze([
  { key: ForestLocationKey.NorthGrove, label: 'Top enemy far', tagKey: 'forest top enemy far', xBasisPoints: 5680, yBasisPoints: 3820 },
  { key: ForestLocationKey.NorthwestGrove, label: 'Top ally near', tagKey: 'forest top ally near', xBasisPoints: 3660, yBasisPoints: 4160 },
  { key: ForestLocationKey.WestGrove, label: 'Top ally far', tagKey: 'forest top ally far', xBasisPoints: 4520, yBasisPoints: 4360 },
  { key: ForestLocationKey.EastGrove, label: 'Top enemy near', tagKey: 'forest top enemy near', xBasisPoints: 6530, yBasisPoints: 4175 },
  { key: ForestLocationKey.SouthwestGrove, label: 'Bottom ally near', tagKey: 'forest bottom ally near', xBasisPoints: 3630, yBasisPoints: 5810 },
  { key: ForestLocationKey.CentralGrove, label: 'Bottom enemy far', tagKey: 'forest bottom enemy far', xBasisPoints: 5675, yBasisPoints: 5720 },
  { key: ForestLocationKey.SoutheastGrove, label: 'Bottom enemy near', tagKey: 'forest bottom enemy near', xBasisPoints: 6495, yBasisPoints: 5740 },
  { key: ForestLocationKey.SouthGrove, label: 'Bottom ally far', tagKey: 'forest bottom ally far', xBasisPoints: 4510, yBasisPoints: 6320 },
])

/** Tree assignment marker, projected from the seeded `tree` tag. */
export const TREE_OBJECTIVE: Readonly<MapTagObjective> = Object.freeze({
  key: 'tree',
  label: 'Tree',
  tagKey: 'tree',
  xBasisPoints: 8000,
  yBasisPoints: 5000,
})

/** Timed Boss assignments are independent seeded-tag projections. */
export const BOSS_OBJECTIVES: ReadonlyArray<MapTagObjective> = Object.freeze([
  {
    key: 'boss-15',
    label: '15 min boss',
    tagKey: '15 min boss',
    xBasisPoints: 5000,
    yBasisPoints: 1200,
  },
  {
    key: 'boss-25',
    label: '25 min boss',
    tagKey: '25 min boss',
    xBasisPoints: 5000,
    yBasisPoints: 8800,
  },
])

const forestLocationKeys = new Set<string>(
  FOREST_LOCATIONS.map((location) => location.key),
)

export function isForestLocationKey(value: string): value is ForestLocationKey {
  return forestLocationKeys.has(value)
}

function offsetLabel(offsetMinutes: number): string {
  return `T+${String(offsetMinutes).padStart(2, '0')}`
}

function localTimeLabel(value: Date): string {
  return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`
}

/**
 * Build the T+05 forest cadence through the raid's duration. When the raid has
 * a valid scheduled start, each label becomes the corresponding local clock
 * time; unscheduled (or malformed) starts retain stable relative labels.
 */
export function forestSpawnSlots(
  scheduledStartsAt: string | null,
  raidDurationMinutes?: number | null,
): ReadonlyArray<ForestSpawnSlot> {
  const throughOffsetMinutes = raidDurationMinutes
    ?? DEFAULT_RAID_DURATION_MINUTES
  const start = scheduledStartsAt === null ? null : new Date(scheduledStartsAt)
  const validStart = start !== null && Number.isFinite(start.getTime()) ? start : null
  const lastOffset = Math.max(
    0,
    Math.floor(throughOffsetMinutes / FOREST_SPAWN_INTERVAL_MINUTES)
      * FOREST_SPAWN_INTERVAL_MINUTES,
  )
  const slots: ForestSpawnSlot[] = []
  let offsetMinutes = FOREST_SPAWN_INTERVAL_MINUTES
  while (offsetMinutes <= lastOffset) {
    if (validStart === null) {
      slots.push({
        offsetMinutes,
        scheduledAt: null,
        label: offsetLabel(offsetMinutes),
      })
    } else {
      const scheduled = new Date(
        validStart.getTime() + offsetMinutes * 60 * 1000,
      )
      slots.push({
        offsetMinutes,
        scheduledAt: scheduled.toISOString(),
        label: localTimeLabel(scheduled),
      })
    }
    offsetMinutes += FOREST_SPAWN_INTERVAL_MINUTES
  }
  return slots
}
